import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import useSmartMergeStore from '../../Features/SmartMerge/store';
import toast from 'react-hot-toast';
const EVENT_NEW_CHAT = 'on_new_chat';
const EVENT_SELECT_CHAT = 'on_select_chat';
const SCRIPT_FOR_TTS = 'script_for_tts';
const LOADING_FAV_TOOLS = 'loading_fav_tools';
const CAPABILITIES_CHANGE = 'capabilities_change';
const REMOVE_MODEL = 'remove_model';
const SAVE_MERGE = 'save_merge';
const EVENT_ADD_MODEL = 'add_model';
// Render types
const TABS = 'tabs';
const COLUMNS = 'columns';
const ROWS = 'rows';
const GRID = 'grid';

const getDefaultRenderTypeCm = () => {
  // Check if window is defined (for SSR compatibility)
  if (typeof window === 'undefined') return COLUMNS;

  // Get physical characteristics
  const pixelRatio = window.devicePixelRatio;

  // Adjust width taking pixel ratio into account
  const adjustedWidth = window.innerWidth / pixelRatio;

  // Calculate if we have enough space for comfortable columns
  const minimumColumnWidth = 290;
  const numberOfColumns = 4;
  const columnWidth = adjustedWidth / numberOfColumns;

  return columnWidth >= minimumColumnWidth ? COLUMNS : GRID;
};

const getDefaultRenderTypePx = () => {
  // Check if window is defined (for SSR compatibility)
  if (typeof window === 'undefined') return TABS;
  const innerWidth = window.innerWidth;
  const trueWidth = innerWidth * 0.9;

  // Calculate if we have enough space for comfortable columns
  const minimumColumnWidth = 280;
  const numberOfColumns = 4;
  const columnWidth = trueWidth / numberOfColumns;

  return columnWidth >= minimumColumnWidth ? COLUMNS : GRID;
};

const useChatStore = create(
  persist(
    (set, get) => ({
      // States
      chatList: [],
      selectedChat: {
        index: null,
        chat: {
          _id: null,
          messages: [],
        },
      },
      loadingChats: false,
      totalPages: 1,
      currentPage: 1,
      totalChats: 0,
      chatWords: 0,
      refFile: undefined,
      powerPanelOpen: false,
      mobilePowerPanel: false,
      selectedModels: [],
      showModelsDialog: false,
      currentEvent: {
        name: null,
        params: {},
      },
      modelsList: [],
      renderType: getDefaultRenderTypePx(),
      maxMessageModels: 1,
      hideOnHover: false,
      headerVisible: true,
      addResponseModels: false,
      replaceResponse: null,
      pannelWidth: 320,
      showMultiFileToggled: false,
      combos: [],
      combosLoading: false,
      selectedCombo: null,
      isEditingCombo: false,
      shareChat: false,
      rawModelsList: [],

      // Functions
      setTotalPages: (totalPages) => {
        set(() => ({
          totalPages,
        }));
      },
      setTotalChats: (totalChats) => {
        set(() => ({
          totalChats,
        }));
      },
      setCurrentPage: (currentPage) => {
        set(() => ({
          currentPage,
        }));
      },
      selectChat: (chatId) => {
        set((state) => ({
          selectedChat: {
            index: (state.chatList.length ? state.chatList : []).findIndex(
              (chat) => chat._id === chatId
            ),
            chat: (state.chatList.length ? state.chatList : []).find(
              (chat) => chat._id === chatId
            ),
          },
        }));
      },
      setSelectedChat: (chat) => {
        useSmartMergeStore.getState().setActive(false);
        set((state) => ({
          selectedChat: {
            ...state.selectedChat,
            chat,
          },
        }));
      },
      cleanSelectedChat: () => {
        useSmartMergeStore.getState().setActive(false);
        set(() => ({
          selectedChat: {
            index: null,
            chat: {
              _id: null,
              messages: [],
            },
          },
        }));
      },
      setChatsList: (newChatData) => {
        set(() => ({
          chatList: newChatData,
        }));
      },
      setLoadingChats: (loading) => {
        set(() => ({
          loadingChats: loading,
        }));
      },
      cleanChatList: () => {
        set(() => ({
          chatList: [],
        }));
      },
      setChatWords: (chatWords) => {
        set(() => ({
          chatWords,
        }));
      },
      setRefFile: (refFile) => {
        set(() => ({
          refFile,
        }));
      },
      updateRefFile: (updateFunc) => {
        set((state) => ({
          refFile: updateFunc(state.refFile),
        }));
      },
      setPowerPanelOpen: (powerPanelOpen) => {
        set(() => ({
          powerPanelOpen,
        }));
      },
      setMobilePowerPanel: (mobilePowerPanel) => {
        set(() => ({
          mobilePowerPanel,
        }));
      },
      updateSelectedModels: (updateFunc) => {
        set((state) => {
          const updatedModels = updateFunc(state.selectedModels);
          get().verifyComboMatch(updatedModels);
          return {
            selectedModels: updatedModels,
          };
        });
      },

      setShowModelsDialog: (showModelsDialog) => {
        set(() => ({
          showModelsDialog,
        }));
      },
      addEvent: (event, params = {}) => {
        set(() => ({
          currentEvent: {
            name: event,
            params: params,
          },
        }));
      },
      removeEvent: () => {
        set(() => ({
          currentEvent: {
            name: null,
            params: {},
          },
        }));
      },
      runEvent: async (callback) => {
        const { currentEvent } = get();
        if (currentEvent && currentEvent.name) {
          const params = Array.isArray(currentEvent.params)
            ? currentEvent.params
            : Object.entries(currentEvent.params).length === 0
            ? []
            : [currentEvent.params];

          params.length === 0 ? await callback() : await callback(...params);
          get().removeEvent();
        }
      },
      setModelsList: (modelsList) => {
        set(() => ({
          modelsList,
        }));
      },
      setRenderType: (renderType) => {
        const previousRenderType = get().renderType;
        const selectedChat = get().selectedChat;
        const appStore = window.store;
        appStore.sendMetricsEvent(
          'change_view',
          { chat: selectedChat.chat._id },
          { from_view: previousRenderType, to_view: renderType }
        );

        set(() => ({
          renderType,
        }));
      },
      setMaxMessageModels: (maxMessageModels) => {
        set(() => ({
          maxMessageModels,
        }));
      },
      setHideOnHover: (hideOnHover) => {
        set(() => ({
          hideOnHover,
        }));
      },
      setHeaderVisible: (headerVisible) => {
        set(() => ({
          headerVisible,
        }));
      },
      setAddResponseModels: (addResponseModels) => {
        set(() => ({
          addResponseModels,
        }));
      },
      setReplaceResponse: (replaceResponse) => {
        set(() => ({
          replaceResponse,
        }));
      },
      setPannelWidth: (pannelWidth) => {
        set(() => ({
          pannelWidth,
        }));
      },
      setShowMultiFileToggled: (showMultiFileToggled) => {
        set(() => ({
          showMultiFileToggled,
        }));
      },
      setIsEditingCombo: (isEditing) => {
        set(() => ({ isEditingCombo: isEditing }));
      },
      setCombos: (combos) => {
        set(() => ({ combos }));
      },
      removeCombo: (comboId) => {
        set((state) => ({
          combos: state.combos.filter((combo) => combo._id !== comboId),
        }));
      },
      verifyComboMatch: (selectedModels) => {
        const { combos, setSelectedCombo, isEditingCombo } = get();
        if (isEditingCombo) return;

        const matchingCombo = combos.find((combo) => {
          const comboModels = combo.llms || [];
          const selectedModelIds = selectedModels
            .map((model) => model._id)
            .sort();
          return (
            comboModels.length === selectedModelIds.length &&
            comboModels
              .sort()
              .every((id, index) => id === selectedModelIds[index])
          );
        });

        if (matchingCombo) {
          setSelectedCombo(matchingCombo);
        } else {
          const customCombo = {
            _id: 'custom',
            name: 'Custom Combination',
            llms: selectedModels.map((model) => model._id),
            isDefault: false,
          };
          setSelectedCombo(customCombo);
        }
      },
      setSelectedCombo: (combo) => {
        set((state) => {
          const updatedCombos = state.combos.map((c) => ({
            ...c,
            isSelected: c._id === combo._id,
          }));

          return {
            selectedCombo: { ...combo, isSelected: true },
            combos: updatedCombos,
          };
        });

        if (combo && combo.llms) {
          const sortedModels = combo.llms
            .map((id) => ({ _id: id }))
            .sort((a, b) => a._id.localeCompare(b._id));
          localStorage.setItem('currentModels', JSON.stringify(sortedModels));
        }
      },

      updateSelectedCombo: async (comboId) => {
        const appStore = window.store;

        try {
          await Promise.all(
            get().combos.map((combo) =>
              appStore.api.put(`/combos/llm-combos/${combo._id}`, {
                isSelected: combo._id === comboId,
              })
            )
          );

          const updatedCombos = get().combos.map((combo) => ({
            ...combo,
            isSelected: combo._id === comboId,
          }));

          const selectedCombo = updatedCombos.find(
            (combo) => combo._id === comboId
          );

          set(() => ({
            combos: updatedCombos,
            selectedCombo,
          }));
        } catch (error) {
          console.error('Error updating selected combo:', error);
        }
      },
      initializeSelectedCombo: () => {
        const { combos, isEditingCombo } = get();
        if (combos.length && !isEditingCombo) {
          const comboAtPosition1 = combos.find((c) => c.position === 1);
          set(() => ({ selectedCombo: comboAtPosition1 || combos[0] }));
        }
      },
      async fetchCombos() {
        set(() => ({ combosLoading: true }));

        try {
          const appStore = window.store;
          const response = await appStore.api.get('/combos/llm-combos');

          // Ensure positions are valid and sequential
          const combos = response.data.combos || [];

          // Remove duplicates based on name
          const uniqueCombos = combos.reduce((acc, combo) => {
            const existingCombo = acc.find((c) => c.name === combo.name);
            if (!existingCombo) {
              acc.push(combo);
            } else if (
              combo.isStraicoDefault &&
              !existingCombo.isStraicoDefault
            ) {
              // Replace non-Straico default with Straico default
              acc = acc.filter((c) => c.name !== combo.name);
              acc.push(combo);
            }
            return acc;
          }, []);

          // Validate positions and sort
          const validatedCombos = uniqueCombos
            .map((combo) => ({
              ...combo,
              // Ensure position is a valid number
              position:
                typeof combo.position === 'number' ? combo.position : 999,
            }))
            .sort((a, b) => a.position - b.position)
            // Re-assign sequential positions
            .map((combo, index) => ({
              ...combo,
              position: index + 1,
            }));

          // If this is a new user (no combos), ensure Starter Kit is first
          if (validatedCombos.length === 0) {
            const starterKit = uniqueCombos.find(
              (c) => c.name === 'Starter Kit'
            );
            if (starterKit) {
              starterKit.position = 1;
              // Move other combos down one position
              validatedCombos.forEach((c) => {
                if (c.name !== 'Starter Kit') {
                  c.position += 1;
                }
              });
            }
          }

          set(() => ({
            combos: validatedCombos,
          }));

          get().initializeSelectedCombo();
        } catch (error) {
          console.error('Error fetching combos:', error);
        } finally {
          set(() => ({ combosLoading: false }));
        }
      },
      updateCombo: async (comboId, updateData) => {
        try {
          const appStore = window.store;
          const response = await appStore.api.put(
            `/combos/llm-combos/${comboId}`,
            updateData
          );

          // Update the combo in the state
          set((state) => ({
            combos: state.combos.map((combo) =>
              combo._id === comboId ? { ...combo, ...updateData } : combo
            ),
          }));

          // Update selectedCombo if it's the combo being updated
          const { selectedCombo } = get();
          if (selectedCombo && selectedCombo._id === comboId) {
            set((state) => ({
              selectedCombo: { ...state.selectedCombo, ...updateData },
            }));
          }

          return response.data.combo;
        } catch (error) {
          console.error('Error updating combo:', error);
          throw new Error('Failed to update combo');
        }
      },
      createCombo: async (comboData) => {
        try {
          const appStore = window.store;
          const response = await appStore.api.post(
            '/combos/llm-combos',
            comboData
          );

          set((state) => ({
            combos: [...state.combos, response.data.combo],
          }));

          set(() => ({
            selectedCombo: response.data.combo,
          }));

          return response.data.combo;
        } catch (error) {
          console.error('Error creating combo:', error);
          throw new Error('Failed to create combo');
        }
      },
      isDefaultCombo: (id) => {
        const combos = get().combos;
        const combo = combos.find((combo) => combo._id === id);
        return combo ? combo.name === 'Starter Kit' : false;
      },
      isStraicoCombo: (id) => {
        const combos = get().combos;
        const combo = combos.find((combo) => combo._id === id);
        return combo ? combo.isStraicoDefault === true : false;
      },
      deleteCombo: async (id) => {
        const appStore = window.store;
        const { combos } = get();

        try {
          const comboToDelete = combos.find((combo) => combo._id === id);
          if (!comboToDelete) {
            throw new Error('Combo not found');
          }

          // Validaciones adicionales
          if (comboToDelete.isDefault || comboToDelete.isStraicoDefault) {
            throw new Error('Cannot delete default combo');
          }

          // Primero intentamos eliminar el combo
          await appStore.api.delete(`/combos/llm-combos/${id}`);

          // Si la eliminación fue exitosa, actualizamos las posiciones
          const updatedCombos = combos
            .filter((combo) => combo._id !== id)
            .map((combo) => {
              if (combo.position > comboToDelete.position) {
                return { ...combo, position: combo.position - 1 };
              }
              return combo;
            });

          // Actualizamos las posiciones en el backend
          try {
            await Promise.all(
              updatedCombos.map((combo) =>
                appStore.api.put(`/combos/llm-combos/${combo._id}`, {
                  position: combo.position,
                })
              )
            );
          } catch (positionError) {
            console.error('Error updating positions:', positionError);
          }

          // Actualizamos el estado local solo si todo fue exitoso
          set(() => ({
            combos: updatedCombos,
          }));
        } catch (error) {
          console.error('Error deleting combo:', error);
          throw error;
        }
      },
      setShareChat: (shareChat) => {
        set(() => ({
          shareChat,
        }));
      },
      isModelProviderError: (message) => {
        return message?.error?.type === 'provider_error';
      },
      setRawModelsList: (rawModelsList) => {
        set(() => ({
          rawModelsList,
        }));
      },
    }),
    {
      name: 'chat-render-type-v1',
      partialize: (state) => ({
        renderType: state.renderType,
        pannelWidth: state.pannelWidth,
      }),
    }
  )
);

export default useChatStore;

export {
  EVENT_NEW_CHAT,
  EVENT_SELECT_CHAT,
  SCRIPT_FOR_TTS,
  LOADING_FAV_TOOLS,
  CAPABILITIES_CHANGE,
  REMOVE_MODEL,
  SAVE_MERGE,
  EVENT_ADD_MODEL,
};

export { TABS, COLUMNS, ROWS, GRID };
